/* eslint-disable react-refresh/only-export-components */
/**
 * Dev Routes
 *
 * Feature-flag-gated routes for development tools, style guides, and
 * billing stubs. Also includes the standalone Event Planner (public, no auth).
 *
 * - VITE_ENABLE_DEV_PAGES: style guide, workspace UI ref, job timeline, synthesis inspector
 * - VITE_ENABLE_BILLING: billing settings page
 * - event-planner: public, no auth required
 */

import { lazy, Suspense } from "react";
import { Route } from "react-router-dom";

import { AuthGate } from "@/components/access/AuthGate";
import { flags } from "@/config/featureFlags";
import UnifiedLayout from "@/layouts/UnifiedLayout";
import Dashboard from "@/pages/Dashboard";
import TestIntakeUpload from "@/pages/TestIntakeUpload";
import { RequireAdmin } from "@/permissions";

// Style guide and dev pages
const StyleGuide = lazy(() => import("@/features/style-guide/StyleGuidePage"));
const WorkspaceUIReference = lazy(
  () => import("@/pages/dev/WorkspaceUIReference"),
);
const JobTimelinePage = lazy(() => import("@/pages/dev/JobTimelinePage"));
const SynthesisInspectorPage = lazy(
  () => import("@/pages/dev/SynthesisInspectorPage"),
);

// Standalone Event Planner (hidden route - not linked in nav)
const EventPlannerRoutes = lazy(
  () => import("@/features/event-planner/EventPlannerRoutes"),
);

// Settings pages (stubbed for future)
const BillingPage = lazy(() => import("@/pages/BillingPage"));

const PageLoader = () => (
  <div className="flex items-center justify-center p-8">
    <div className="text-gray-600">Loading...</div>
  </div>
);

export function devRoutes() {
  return (
    <>
      {/* =================================================================== */}
      {/* DEV/LEGACY ROUTES - Gated behind VITE_ENABLE_DEV_PAGES             */}
      {/* =================================================================== */}

      {flags.enableDevPages && (
        <Route
          element={
            <AuthGate>
              <RequireAdmin redirectTo="/home">
                <UnifiedLayout />
              </RequireAdmin>
            </AuthGate>
          }
        >
          {/* Legacy dashboard */}
          <Route path="dashboard" element={<Dashboard />} />
          <Route path="test-intake-upload" element={<TestIntakeUpload />} />
          <Route
            path="style-guide"
            element={
              <Suspense fallback={<PageLoader />}>
                <StyleGuide />
              </Suspense>
            }
          />
          {/* Dev-only: UI visual reference */}
          <Route
            path="dev/workspace-ui"
            element={
              <Suspense fallback={<PageLoader />}>
                <WorkspaceUIReference />
              </Suspense>
            }
          />
          {/* Dev-only: Job timeline viewer */}
          <Route
            path="dev/jobs"
            element={
              <Suspense fallback={<PageLoader />}>
                <JobTimelinePage />
              </Suspense>
            }
          />
          <Route
            path="dev/jobs/:jobId"
            element={
              <Suspense fallback={<PageLoader />}>
                <JobTimelinePage />
              </Suspense>
            }
          />
          {/* Dev-only: Synthesis Inspector */}
          <Route
            path="dev/synthesis-inspector"
            element={
              <Suspense fallback={<div />}>
                <SynthesisInspectorPage />
              </Suspense>
            }
          />
        </Route>
      )}

      {/* =================================================================== */}
      {/* BILLING - Gated behind VITE_ENABLE_BILLING (future feature)        */}
      {/* =================================================================== */}

      {flags.enableBilling && (
        <Route
          element={
            <AuthGate>
              <UnifiedLayout />
            </AuthGate>
          }
        >
          <Route
            path="settings/billing"
            element={
              <Suspense fallback={<PageLoader />}>
                <BillingPage />
              </Suspense>
            }
          />
        </Route>
      )}

      {/* =================================================================== */}
      {/* STANDALONE EVENT PLANNER - Public route (no auth required)        */}
      {/* Access at /event-planner for non-client event planning            */}
      {/* =================================================================== */}

      <Route
        path="event-planner/*"
        element={
          <Suspense fallback={<PageLoader />}>
            <EventPlannerRoutes />
          </Suspense>
        }
      />
    </>
  );
}
